'use client';
import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, ExternalLink } from 'lucide-react';


interface ManualAd {
  id: string;
  imageUrl: string;
  linkUrl: string;
  title: string;
  altText: string;
}

interface AdSpotProps {
  slot?: string;
  format?: 'horizontal' | 'rectangle' | 'vertical';
  className?: string;
}

// AdSense client from env
const ADSENSE_CLIENT = process.env.NEXT_PUBLIC_ADSENSE_CLIENT || '';

// Simple local storage key for manual ads (admin-uploaded)
const MANUAL_ADS_KEY = 'golive_manual_ads';

function loadManualAds(): ManualAd[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(MANUAL_ADS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveManualAds(ads: ManualAd[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MANUAL_ADS_KEY, JSON.stringify(ads));
}

// ─── Admin Ad Upload Panel ────────────────────────────────────────────────────
export function AdManagementPanel() {
  const [ads, setAds] = useState<ManualAd[]>([]);
  const [form, setForm] = useState({ title: '', linkUrl: '', altText: '' });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setAds(loadManualAds());
  }, []);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('Only PNG, JPEG, GIF, or WebP images are allowed.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB.');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!preview || !form.title) return;
    setSaving(true);
    const newAd: ManualAd = {
      id: `ad-${Date.now()}`,
      imageUrl: preview,
      linkUrl: form.linkUrl || '#',
      title: form.title,
      altText: form.altText || form.title,
    };
    const updated = [...ads, newAd];
    setAds(updated);
    saveManualAds(updated);
    setForm({ title: '', linkUrl: '', altText: '' });
    setImageFile(null);
    setPreview('');
    setSaving(false);
  };

  const removeAd = (id: string) => {
    const updated = ads.filter((a) => a.id !== id);
    setAds(updated);
    saveManualAds(updated);
  };

  return (
    <div className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl p-4 space-y-4">
      <h3 className="text-sm font-700 text-white">Ad Management</h3>

      {/* Upload Form */}
      <div className="space-y-3">
        <div>
          <label className="text-xs text-[rgb(163,163,163)] mb-1 block">Ad Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Sponsor Banner"
            className="w-full bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] text-sm text-white placeholder-[rgb(115,115,115)] rounded-lg px-3 py-2 focus:outline-none focus:border-red-600"
          />
        </div>
        <div>
          <label className="text-xs text-[rgb(163,163,163)] mb-1 block">Destination URL</label>
          <input
            type="url"
            value={form.linkUrl}
            onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
            placeholder="https://advertiser.com"
            className="w-full bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] text-sm text-white placeholder-[rgb(115,115,115)] rounded-lg px-3 py-2 focus:outline-none focus:border-red-600"
          />
        </div>
        <div>
          <label className="text-xs text-[rgb(163,163,163)] mb-1 block">Alt Text</label>
          <input
            type="text"
            value={form.altText}
            onChange={(e) => setForm({ ...form, altText: e.target.value })}
            placeholder="Describe the ad image"
            className="w-full bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] text-sm text-white placeholder-[rgb(115,115,115)] rounded-lg px-3 py-2 focus:outline-none focus:border-red-600"
          />
        </div>
        <div>
          <label className="text-xs text-[rgb(163,163,163)] mb-1 block">Ad Image (PNG/JPEG/GIF/WebP, max 2MB)</label>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleFile} className="hidden" />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] hover:border-red-600 text-[rgb(163,163,163)] hover:text-white text-sm rounded-lg transition-all"
          >
            <Upload size={14} />
            {imageFile ? imageFile.name : 'Choose Image'}
          </button>
          {preview && (
            <img src={preview} alt="Ad preview" className="mt-2 h-20 rounded-lg object-cover border border-[rgb(38,38,38)]" />
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={!preview || !form.title || saving}
          className="w-full py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-600 rounded-lg transition-all"
        >
          {saving ? 'Saving...' : 'Upload Ad'}
        </button>
      </div>

      {/* Existing Ads */}
      {ads.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-600 text-[rgb(163,163,163)] uppercase tracking-wide">Active Ads ({ads.length})</p>
          {ads.map((ad) => (
            <div key={ad.id} className="flex items-center gap-3 bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] rounded-lg p-2">
              <img src={ad.imageUrl} alt={ad.altText} className="w-16 h-10 object-cover rounded flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 text-white truncate">{ad.title}</p>
                <p className="text-[10px] text-[rgb(115,115,115)] truncate">{ad.linkUrl}</p>
              </div>
              <button onClick={() => removeAd(ad.id)} className="p-1 text-[rgb(115,115,115)] hover:text-red-500 transition-colors flex-shrink-0">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ad Spot Component ────────────────────────────────────────────────────────
export default function AdSpot({ slot = '1234567890', format = 'horizontal', className = '' }: AdSpotProps) {
  const adRef = useRef<HTMLDivElement>(null);
  const [manualAd, setManualAd] = useState<ManualAd | null>(null);
  const [adLoaded, setAdLoaded] = useState(false);

  useEffect(() => {
    // Try to show a manual ad first
    const ads = loadManualAds();
    if (ads.length > 0) {
      const randomAd = ads[Math.floor(Math.random() * ads.length)];
      setManualAd(randomAd);
      return;
    }

    // Otherwise load Google AdSense
    if (!ADSENSE_CLIENT) return;
    try {
      const adsbygoogle = (window as any).adsbygoogle || [];
      adsbygoogle.push({});
      setAdLoaded(true);
    } catch {
      // AdSense not available
    }
  }, []);

  const heightClass = format === 'horizontal' ? 'h-24' : format === 'rectangle' ? 'h-48' : 'h-64';

  // Manual ad display
  if (manualAd) {
    return (
      <div className={`relative rounded-xl overflow-hidden border border-[rgb(38,38,38)] ${heightClass} ${className}`}>
        <a href={manualAd.linkUrl} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
          <img src={manualAd.imageUrl} alt={manualAd.altText} className="w-full h-full object-cover" />
          <div className="absolute bottom-1 right-2 text-[9px] text-[rgb(115,115,115)] bg-black/60 px-1.5 py-0.5 rounded">
            Ad
          </div>
        </a>
      </div>
    );
  }

  // Google AdSense or placeholder
  if (ADSENSE_CLIENT) {
    return (
      <div className={`relative rounded-xl overflow-hidden border border-[rgb(38,38,38)] bg-[rgb(15,15,15)] ${heightClass} ${className}`} ref={adRef}>
        <ins
          className="adsbygoogle"
          style={{ display: 'block', width: '100%', height: '100%' }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slot}
          data-ad-format={format === 'horizontal' ? 'horizontal' : 'rectangle'}
          data-full-width-responsive="true"
        />
        <div className="absolute bottom-1 right-2 text-[9px] text-[rgb(115,115,115)]">Ad</div>
      </div>
    );
  }

  // Placeholder when no ad configured
  return (
    <div className={`relative rounded-xl overflow-hidden border border-dashed border-[rgb(38,38,38)] bg-[rgb(15,15,15)] ${heightClass} ${className} flex items-center justify-center`}>
      <div className="text-center">
        <ExternalLink size={16} className="text-[rgb(64,64,64)] mx-auto mb-1" />
        <p className="text-[10px] text-[rgb(64,64,64)]">Advertisement</p>
      </div>
    </div>
  );
}
